import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Typography,
  Card,
  Button,
  DatePicker,
  Select,
  Table,
  message,
  Tabs,
  Statistic,
  Row,
  Col,
  Spin,
  Tag,
  Switch,
  Badge,
  notification,
  Space,
} from "antd";
import {
  DownloadOutlined,
  ShoppingOutlined,
  InboxOutlined,
  SyncOutlined,
  FullscreenOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  AreaChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  TeamOutlined,
  TableOutlined,
  BookOutlined,
  FireOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import {
  fetchBookingsForExport,
  prepareBookingExportData,
  getExportFilename,
} from "../services/bookingExportService";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ScatterChart,
  Scatter,
  ZAxis,
  Brush,
} from "recharts";

// Configure dayjs to use weekday and isoWeek plugins
dayjs.extend(weekday);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

// Styled components
const Container = styled.div`
  padding: 24px;
`;

const DashboardCard = styled(Card)`
  margin-bottom: 24px;
  height: 100%;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  margin-bottom: 20px;
`;

const FiltersContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const StatsCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
`;

const CenteredSpin = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

// New styled components for enhanced dashboard
const FullScreenContainer = styled.div`
  &.fullscreen-dashboard {
    background-color: white;

    .ant-tabs-content {
      display: flex;
      flex-direction: column;
      height: calc(100vh - 180px);
      overflow: auto;
    }

    .dashboard-metrics {
      flex-shrink: 0;
    }

    .dashboard-charts {
      flex-grow: 1;
      overflow: hidden;
    }

    .dashboard-metrics .ant-statistic-title {
      font-size: 12px;
    }

    .dashboard-metrics .ant-statistic-content {
      font-size: 20px;
    }
  }

  /* Target the fullscreen element to ensure white background */
  &:fullscreen {
    background-color: white;
  }
`;

const LastUpdateBadge = styled.div`
  display: flex;
  align-items: center;
  background: rgba(24, 144, 255, 0.1);
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
`;

const NoDataMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #8c8c8c;
  text-align: center;
`;

// Color constants for charts
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FF6B3B",
  "#A569BD",
];
const STATUS_COLORS: Record<string, string> = {
  draft: "#8c8c8c",
  pending: "#faad14",
  confirmed: "#1890ff",
  in_transit: "#52c41a",
  delivered: "#52c41a",
  cancelled: "#f5222d",
};

// Define the type for topPerformingCompanies table rows
type TopCompany = {
  company: string;
  bookings: number;
  volume: number;
  avgVolume: number;
  weight: number;
  avgWeight: number;
};

// Table row type
interface BookingRow {
  ID: string | number;
  Reference: string;
  Status: string | null;
  CreatedDate: string;
  Company: string;
  TransportType: string;
  TotalCBM: string | number;
  TotalWeight: string | number;
  IsConsolidated: string;
  created_at?: string;
  [key: string]: string | number | null | undefined;
}

// Add this helper above the component
type ScatterLabelProps = { value: number; cx: number; cy: number };
const renderScatterLabel = (props: ScatterLabelProps) => {
  const { value, cx, cy } = props;
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={14}
      fontWeight="bold"
      fill="#333"
      style={{ pointerEvents: "none" }}
    >
      {value && value > 0 ? value : ""}
    </text>
  );
};

// Simplify the deterministic hour function - only used as a fallback
const generateDeterministicHour = (identifier: string): number => {
  // Generate a simple hash value from the identifier string
  const hash = identifier
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  // Generate a business hour between 9-17 (9am to 5pm)
  return 9 + (hash % 9);
};

// Helper function to convert UTC timestamp to local time
const convertUTCToLocal = (timestamp: string): dayjs.Dayjs => {
  // Parse as UTC, then convert to local time
  const localDate = dayjs.utc(timestamp).local();
  return localDate;
};

// Helper function to get the current date in local timezone and convert to UTC ISO string
const getUTCDateString = (localDate: dayjs.Dayjs): string => {
  return localDate.utc().format("YYYY-MM-DD");
};

// Add a function to prepare date filters for backend
const getDateRangeForBackend = (
  range: string
): [string, string] | undefined => {
  if (range === "all-time" || range === "custom") {
    return undefined;
  }

  // Get today in local timezone
  const localToday = dayjs().tz(dayjs.tz.guess());
  let startDate: dayjs.Dayjs;
  let endDate: dayjs.Dayjs;

  switch (range) {
    case "today":
      startDate = localToday.startOf("day");
      endDate = localToday.endOf("day");
      break;
    case "yesterday":
      startDate = localToday.subtract(1, "days").startOf("day");
      endDate = localToday.subtract(1, "days").endOf("day");
      break;
    case "this-week":
      startDate = localToday.startOf("isoWeek");
      endDate = localToday.endOf("isoWeek");
      break;
    case "last-week":
      startDate = localToday.subtract(1, "weeks").startOf("isoWeek");
      endDate = localToday.subtract(1, "weeks").endOf("isoWeek");
      break;
    case "this-month":
      startDate = localToday.startOf("month");
      endDate = localToday.endOf("month");
      break;
    case "last-month":
      startDate = localToday.subtract(1, "month").startOf("month");
      endDate = localToday.subtract(1, "month").endOf("month");
      break;
    case "month-to-date":
      startDate = localToday.startOf("month");
      endDate = localToday.endOf("day");
      break;
    case "year-to-date":
      startDate = localToday.startOf("year");
      endDate = localToday.endOf("day");
      break;
    case "last-quarter":
      startDate = localToday.subtract(3, "months").startOf("day");
      endDate = localToday;
      break;
    case "last-year":
      startDate = localToday.subtract(1, "year").startOf("day");
      endDate = localToday;
      break;
    default:
      startDate = localToday.startOf("month");
      endDate = localToday.endOf("month");
  }

  console.log(
    `Date range for backend (${range}): ${startDate.format(
      "YYYY-MM-DD"
    )} to ${endDate.format("YYYY-MM-DD")}`
  );

  // Convert local dates to UTC for backend filtering
  return [getUTCDateString(startDate), getUTCDateString(endDate)];
};

/**
 * Reports Admin Page
 */
const ReportsAdmin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState<BookingRow[]>([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("this-month");
  const [filters, setFilters] = useState({
    dateRange: undefined as [string, string] | undefined,
    status: undefined as string | undefined,
    transportTypeId: undefined as string | undefined,
    isConsolidation: undefined as boolean | undefined,
  });
  const [realTimeEnabled, setRealTimeEnabled] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>(
    dayjs().format("HH:mm:ss")
  );
  const [newBookingsCount, setNewBookingsCount] = useState(0);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastDataCountRef = useRef(0);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Track page view
  useEffect(() => {
    trackFeatureUsage("reports_admin");

    // Load bookings with default time range
    setTimeRange("this-month");
    loadBookings();

    // Cleanup function for auto-refresh interval
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, []);

  // Effect for handling real-time monitoring
  useEffect(() => {
    if (realTimeEnabled) {
      // Start auto-refresh interval (every 30 seconds)
      autoRefreshIntervalRef.current = setInterval(() => {
        refreshData();
      }, 30000); // 30 seconds

      // Set initial reference count
      lastDataCountRef.current = exportData.length;
    } else {
      // Clear interval when real-time disabled
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    }

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [realTimeEnabled, exportData.length]);

  // Replace loadAllBookings with one that uses backend filtering
  const loadBookings = async () => {
    setLoading(true);
    try {
      // Prepare date range filter based on the time range
      const dateRange = timeRange
        ? getDateRangeForBackend(timeRange)
        : undefined;

      console.log(
        `Loading bookings with backend filtering: ${JSON.stringify({
          dateRange,
          status: filters.status,
          transportTypeId: filters.transportTypeId,
          isConsolidation: filters.isConsolidation,
        })}`
      );

      // Call fetchBookingsForExport with date range filter
      const { data, error } = await fetchBookingsForExport({
        dateRange,
        status: filters.status,
        transportTypeId: filters.transportTypeId,
        isConsolidation: filters.isConsolidation,
      });

      if (error) throw error;

      const preparedData = prepareBookingExportData(data);
      setExportData(preparedData);
      setLastUpdated(dayjs().format("HH:mm:ss"));
      lastDataCountRef.current = preparedData.length;

      // Update filters.dateRange for UI date picker
      if (dateRange) {
        setFilters({
          ...filters,
          dateRange,
        });
      }
    } catch (err) {
      console.error("Error loading booking data:", err);
      message.error("Failed to load booking data");
    } finally {
      setLoading(false);
    }
  };

  // Replace the refreshData function to use backend filtering
  const refreshData = useCallback(async () => {
    try {
      // Prepare date range filter based on the time range
      const dateRange = timeRange
        ? getDateRangeForBackend(timeRange)
        : undefined;

      // Call fetchBookingsForExport with date range filter
      const { data, error } = await fetchBookingsForExport({
        dateRange,
        status: filters.status,
        transportTypeId: filters.transportTypeId,
        isConsolidation: filters.isConsolidation,
      });

      if (error) throw error;

      const preparedData = prepareBookingExportData(data);
      setLastUpdated(dayjs().format("HH:mm:ss"));

      // Check if there are new bookings
      const previousCount = lastDataCountRef.current;
      const newCount = preparedData.length;

      if (newCount > previousCount) {
        const diff = newCount - previousCount;
        setNewBookingsCount(diff);
        notification.info({
          message: "New Bookings Detected",
          description: `${diff} new booking${
            diff > 1 ? "s" : ""
          } received since last refresh.`,
          placement: "topRight",
        });
      }

      lastDataCountRef.current = newCount;
      setExportData(preparedData);

      // Update filters.dateRange for UI date picker
      if (dateRange) {
        setFilters({
          ...filters,
          dateRange,
        });
      }
    } catch (err) {
      console.error("Error refreshing data:", err);
      message.error("Failed to refresh data");
    }
  }, [
    filters.status,
    filters.transportTypeId,
    filters.isConsolidation,
    timeRange,
  ]);

  // Update the handleTimeRangeChange function to properly update both timeRange and dateRange
  const handleTimeRangeChange = (value: string) => {
    // First update the timeRange state
    setTimeRange(value);

    if (value === "all-time") {
      // Clear date filters and reload data
      setFilters({
        ...filters,
        dateRange: undefined,
      });

      // Load all data without date filtering
      loadBookings();
    } else {
      // Get date range for backend filtering
      const dateRange = getDateRangeForBackend(value);

      if (dateRange) {
        // Directly update filters with the new date range
        setFilters((prev) => ({
          ...prev,
          dateRange,
        }));

        console.log(
          `Changing date range for ${value}: ${dateRange[0]} to ${dateRange[1]}`
        );

        // Load bookings with this date range
        fetchBookingsForExport({
          dateRange,
          status: filters.status,
          transportTypeId: filters.transportTypeId,
          isConsolidation: filters.isConsolidation,
        }).then(({ data, error }) => {
          if (error) {
            console.error("Error loading data with new date range:", error);
            message.error("Failed to load booking data");
            return;
          }

          const preparedData = prepareBookingExportData(data);
          setExportData(preparedData);
          setLastUpdated(dayjs().format("HH:mm:ss"));
          lastDataCountRef.current = preparedData.length;
        });
      }
    }
  };

  // Simplify getFilteredBookings since we're now filtering on the backend
  const getFilteredBookings = () => {
    return exportData;
  };

  const downloadCSV = () => {
    if (exportData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      // Create CSV header
      const headers = Object.keys(exportData[0]).join(",");

      // Create CSV rows
      const rows = exportData
        .map((row) => {
          return Object.values(row)
            .map((value) => {
              // Handle null values
              if (value === null) {
                return "";
              }
              // Handle strings with commas by quoting
              if (typeof value === "string" && value.includes(",")) {
                return `"${value}"`;
              }
              return value;
            })
            .join(",");
        })
        .join("\n");

      // Combine header and rows
      const csvContent = `${headers}\n${rows}`;

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", getExportFilename());
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("CSV file downloaded successfully");
      trackFeatureUsage("reports_admin_export_csv");
    } catch (err) {
      console.error("Error downloading CSV:", err);
      message.error("Failed to download CSV");
    }
  };

  // Processed data for visualizations
  const bookingsByStatus = useMemo(() => {
    const statuses: Record<string, number> = {};
    getFilteredBookings().forEach((booking) => {
      const status = String(booking.Status);
      statuses[status] = (statuses[status] || 0) + 1;
    });
    return Object.entries(statuses).map(([name, value]) => ({ name, value }));
  }, [exportData, filters.dateRange]);

  // Update the time granularity function to include hourly for single day
  const getTimeGranularity = () => {
    if (!filters.dateRange) return "month";

    const startDate = dayjs(filters.dateRange[0]);
    const endDate = dayjs(filters.dateRange[1]);
    const durationDays = endDate.diff(startDate, "day");

    if (durationDays <= 1) {
      // Same day or just 1 day difference - use hourly granularity
      return "hour";
    } else if (durationDays <= 31) {
      return "day";
    } else if (durationDays <= 90) {
      return "week";
    } else {
      return "month";
    }
  };

  // Update the bookingTimeSeries useMemo to group by status for stacked bar chart
  const bookingTimeSeries = useMemo(() => {
    const granularity = getTimeGranularity();
    const timeData: Record<string, Record<string, number>> = {};
    const allStatuses = new Set<string>();

    // Get filtered bookings
    const filteredBookings = getFilteredBookings();

    // Debug timezone offset
    const tzOffset = new Date().getTimezoneOffset();
    console.log(
      `Local timezone offset: ${tzOffset} minutes (${tzOffset / 60} hours)`
    );

    // For debugging, log a few bookings with their converted times
    if (filteredBookings.length > 0) {
      const sample = filteredBookings.slice(0, 5);
      console.log("Sample bookings with timezone conversion:");
      sample.forEach((booking) => {
        if (booking.created_at) {
          const utcTime = booking.created_at;
          const localTime = convertUTCToLocal(utcTime).format(
            "YYYY-MM-DD HH:mm:ss"
          );
          console.log(
            `ID: ${booking.ID}, Status: ${booking.Status}, UTC: ${utcTime}, Local: ${localTime}`
          );
        }
      });
    }

    // Initialize with empty time periods
    if (filters.dateRange) {
      const start = dayjs(filters.dateRange[0]);
      const end = dayjs(filters.dateRange[1]);

      let current = start;
      if (granularity === "hour") {
        current = current.startOf("day");
        const endOfDay = end.endOf("day");

        while (current.isBefore(endOfDay) || current.isSame(endOfDay, "hour")) {
          const key = current.format("YYYY-MM-DD HH:00");
          timeData[key] = {};
          current = current.add(1, "hour");
        }
      } else {
        while (current.isBefore(end) || current.isSame(end, "day")) {
          let key: string;

          if (granularity === "day") {
            key = current.format("YYYY-MM-DD");
          } else if (granularity === "week") {
            key = current.startOf("isoWeek").format("YYYY-MM-DD");
          } else {
            key = current.format("YYYY-MM");
          }

          if (!timeData[key]) {
            timeData[key] = {};
          }

          if (granularity === "day") {
            current = current.add(1, "day");
          } else if (granularity === "week") {
            current = current.add(1, "week");
          } else {
            current = current.add(1, "month");
          }
        }
      }
    }

    // Fill in actual data
    filteredBookings.forEach((booking) => {
      // Get the booking status
      const status = String(booking.Status || "unknown");
      // Add to our set of all statuses
      allStatuses.add(status);

      // Always prefer created_at if it's available and has valid time
      if (booking.created_at && typeof booking.created_at === "string") {
        // Convert from UTC to local time
        const date = convertUTCToLocal(booking.created_at);

        // Skip invalid dates
        if (!date.isValid()) return;

        let key: string;
        if (granularity === "hour") {
          key = date.format("YYYY-MM-DD HH:00");
        } else if (granularity === "day") {
          key = date.format("YYYY-MM-DD");
        } else if (granularity === "week") {
          key = date.startOf("isoWeek").format("YYYY-MM-DD");
        } else {
          key = date.format("YYYY-MM");
        }

        if (key in timeData) {
          // Initialize status count if needed
          if (!timeData[key][status]) {
            timeData[key][status] = 0;
          }
          // Increment count for this status
          timeData[key][status] += 1;
        }
      }
      // Fall back to CreatedDate when created_at is not available
      else if (booking.CreatedDate) {
        // Treat CreatedDate as UTC and convert to local
        const createdDate = String(booking.CreatedDate);
        let date = convertUTCToLocal(createdDate);

        // Skip invalid dates
        if (!date.isValid()) return;

        // Only apply deterministic hour if we need hourly granularity
        if (granularity === "hour") {
          const identifier = String(booking.ID || booking.Reference || "");
          const deterministicHour = generateDeterministicHour(identifier);
          date = date.hour(deterministicHour);
        }

        let key: string;
        if (granularity === "hour") {
          key = date.format("YYYY-MM-DD HH:00");
        } else if (granularity === "day") {
          key = date.format("YYYY-MM-DD");
        } else if (granularity === "week") {
          key = date.startOf("isoWeek").format("YYYY-MM-DD");
        } else {
          key = date.format("YYYY-MM");
        }

        if (key in timeData) {
          // Initialize status count if needed
          if (!timeData[key][status]) {
            timeData[key][status] = 0;
          }
          // Increment count for this status
          timeData[key][status] += 1;
        }
      }
    });

    // Convert to format suitable for stacked bar chart
    const result = Object.entries(timeData)
      .map(([date, statusCounts]) => {
        // Create a total count
        const totalCount = Object.values(statusCounts).reduce(
          (sum, count) => sum + count,
          0
        );

        // Format the label based on granularity
        let label: string;
        if (granularity === "hour") {
          // For hourly view, use more user-friendly format (e.g., "2 PM" instead of "14:00")
          label = dayjs(date).format("ha"); // Format as "1am", "2pm", etc.
          const fullLabel = dayjs(date).format("MMM D, ha"); // For tooltip

          // Return an object with date, label, and counts by status
          return {
            date, // Keep the original date for sorting
            label, // Shorter format for display
            fullLabel, // Full format for tooltip
            total: totalCount,
            ...statusCounts, // Spread all status counts into the object
          };
        } else if (granularity === "day") {
          label = dayjs(date).format("MMM DD");
        } else if (granularity === "week") {
          const weekStart = dayjs(date).startOf("isoWeek");
          const weekEnd = weekStart.add(6, "days");
          label = `${weekStart.format("MMM DD")}-${weekEnd.format("DD")}`;
        } else {
          label = dayjs(date).format("MMM YYYY");
        }

        return {
          date, // Keep the original date for sorting
          label, // Formatted label for display
          total: totalCount,
          ...statusCounts, // Spread all status counts into the object
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date))
      // Filter out future dates with 0 bookings to avoid confusion
      .filter((item) => {
        const itemDate = dayjs(item.date);
        const now = dayjs();
        // Keep the item if it has data or if it's in the past/present
        return (
          item.total > 0 ||
          itemDate.isBefore(now, "day") ||
          itemDate.isSame(now, "day")
        );
      });

    // Return both the data and the list of all statuses found
    return {
      data: result,
      statuses: Array.from(allStatuses),
    };
  }, [exportData, filters.dateRange]);

  const totalVolume = useMemo(() => {
    return getFilteredBookings()
      .reduce((sum, booking) => {
        const cbm = booking.TotalCBM ? parseFloat(String(booking.TotalCBM)) : 0;
        return sum + cbm;
      }, 0)
      .toFixed(2);
  }, [exportData, filters.dateRange]);

  const totalWeight = useMemo(() => {
    return getFilteredBookings()
      .reduce((sum, booking) => {
        const weight = booking.TotalWeight
          ? parseFloat(String(booking.TotalWeight))
          : 0;
        return sum + weight;
      }, 0)
      .toFixed(2);
  }, [exportData, filters.dateRange]);

  // Calculate average volume per booking
  const averageVolumePerBooking = useMemo(() => {
    const filteredBookings = getFilteredBookings();
    if (filteredBookings.length === 0) {
      return 0;
    }

    const totalVolume = filteredBookings.reduce(
      (sum, booking) => sum + parseFloat(String(booking.TotalCBM) || "0"),
      0
    );

    return (totalVolume / filteredBookings.length).toFixed(2);
  }, [exportData, filters.dateRange]);

  // Calculate average bookings per company
  const bookingsPerCompany = useMemo(() => {
    const filteredBookings = getFilteredBookings();
    if (filteredBookings.length === 0) {
      return {
        average: 0,
        companies: [],
      };
    }

    // Group bookings by company
    const companiesMap: Record<string, number> = {};

    filteredBookings.forEach((booking) => {
      const company = String(booking.Company);
      companiesMap[company] = (companiesMap[company] || 0) + 1;
    });

    // Calculate the average
    const companies = Object.keys(companiesMap);
    const totalCompanies = companies.length;
    const average =
      totalCompanies > 0
        ? (filteredBookings.length / totalCompanies).toFixed(2)
        : "0";

    // Create data for visualization
    const companyData = companies
      .map((company) => ({
        name: company,
        bookings: companiesMap[company],
      }))
      .sort((a, b) => b.bookings - a.bookings);

    return {
      average,
      companies: companyData,
    };
  }, [exportData, filters.dateRange]);

  // Calculate estimated products per booking
  const productsPerBooking = useMemo(() => {
    const filteredBookings = getFilteredBookings();
    if (filteredBookings.length === 0) {
      return {
        average: 0,
        distribution: [],
      };
    }

    // Simulate the number of products by deriving from volume
    // In a real app, you would get the actual product count from the database
    const productsEstimate: Record<string, number> = {};

    filteredBookings.forEach((booking) => {
      const volume = parseFloat(String(booking.TotalCBM) || "0");
      // Rough estimate: 1 m³ ≈ 2-3 products on average
      // Small packages (< 0.5 m³) typically have 1-2 products
      // Medium packages (0.5-2 m³) have 2-4 products
      // Large shipments (> 2 m³) might have 5+ products
      let estimatedProducts = 1; // Minimum one product

      if (volume < 0.5) {
        estimatedProducts = Math.max(1, Math.round(volume * 3)); // 1-2 products
      } else if (volume < 2) {
        estimatedProducts = Math.max(2, Math.round(volume * 2)); // 2-4 products
      } else {
        estimatedProducts = Math.max(5, Math.round(volume * 1.5)); // 5+ products
      }

      const key = String(estimatedProducts);
      productsEstimate[key] = (productsEstimate[key] || 0) + 1;
    });

    // Calculate average
    const totalEstimatedProducts = Object.entries(productsEstimate).reduce(
      (sum, [products, count]) => sum + parseInt(products) * count,
      0
    );

    const average = (totalEstimatedProducts / filteredBookings.length).toFixed(
      1
    );

    // Create distribution data for visualization
    const distribution = Object.entries(productsEstimate)
      .map(([products, count]) => ({
        products: parseInt(products),
        count,
        percentage: ((count / filteredBookings.length) * 100).toFixed(1),
      }))
      .sort((a, b) => a.products - b.products);

    return {
      average,
      distribution,
    };
  }, [exportData, filters.dateRange]);

  // Identify top performing companies
  const topPerformingCompanies = useMemo(() => {
    const filteredBookings = getFilteredBookings();
    if (filteredBookings.length === 0) {
      return [];
    }

    // Group bookings by company and calculate metrics
    const companyMetrics: Record<
      string,
      {
        bookings: number;
        volume: number;
        weight: number;
        avgVolume: number;
        avgWeight: number;
      }
    > = {};

    filteredBookings.forEach((booking) => {
      const company = String(booking.Company);
      const volume = parseFloat(String(booking.TotalCBM) || "0");
      const weight = parseFloat(String(booking.TotalWeight) || "0");

      if (!companyMetrics[company]) {
        companyMetrics[company] = {
          bookings: 0,
          volume: 0,
          weight: 0,
          avgVolume: 0,
          avgWeight: 0,
        };
      }

      companyMetrics[company].bookings += 1;
      companyMetrics[company].volume += volume;
      companyMetrics[company].weight += weight;
    });

    // Calculate averages
    Object.keys(companyMetrics).forEach((company) => {
      const metrics = companyMetrics[company];
      metrics.avgVolume = metrics.volume / metrics.bookings;
      metrics.avgWeight = metrics.weight / metrics.bookings;
    });

    // Convert to array and sort by number of bookings
    return Object.entries(companyMetrics)
      .map(([company, metrics]) => ({
        company,
        ...metrics,
        avgVolume: parseFloat(metrics.avgVolume.toFixed(2)),
        avgWeight: parseFloat(metrics.avgWeight.toFixed(2)),
      }))
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, 12); // Top 12 companies
  }, [exportData, filters.dateRange]);

  // Add period comparison metrics
  const periodComparison = useMemo(() => {
    // Get the current date range
    if (!filters.dateRange) {
      return { data: [], currentPeriod: "", previousPeriod: "", noData: true };
    }

    const currentStart = dayjs(filters.dateRange[0]);
    const currentEnd = dayjs(filters.dateRange[1]);
    const currentDuration = currentEnd.diff(currentStart, "day");

    // Calculate the previous period with the same duration
    const previousStart = currentStart.subtract(currentDuration + 1, "day");
    const previousEnd = previousStart.add(currentDuration, "day");

    // Format for display
    const currentPeriod = `${currentStart.format(
      "MMM D"
    )} - ${currentEnd.format("MMM D")}`;
    const previousPeriod = `${previousStart.format(
      "MMM D"
    )} - ${previousEnd.format("MMM D")}`;

    // Group bookings by period
    const currentPeriodBookings = getFilteredBookings().filter((booking) => {
      if (!booking.created_at && !booking.CreatedDate) return false;

      const date = booking.created_at
        ? dayjs(booking.created_at)
        : dayjs(String(booking.CreatedDate));

      return (
        date.isAfter(currentStart) && date.isBefore(currentEnd.add(1, "day"))
      );
    });

    // For previous period, we need to manually filter the original data
    const previousPeriodBookings = exportData.filter((booking) => {
      if (!booking.created_at && !booking.CreatedDate) return false;

      const date = booking.created_at
        ? dayjs(booking.created_at)
        : dayjs(String(booking.CreatedDate));

      return (
        date.isAfter(previousStart) && date.isBefore(previousEnd.add(1, "day"))
      );
    });

    // Calculate metrics for each period
    const currentMetrics = {
      bookings: currentPeriodBookings.length,
      volume: currentPeriodBookings.reduce(
        (sum, b) => sum + parseFloat(String(b.TotalCBM) || "0"),
        0
      ),
      weight: currentPeriodBookings.reduce(
        (sum, b) => sum + parseFloat(String(b.TotalWeight) || "0"),
        0
      ),
    };

    const previousMetrics = {
      bookings: previousPeriodBookings.length,
      volume: previousPeriodBookings.reduce(
        (sum, b) => sum + parseFloat(String(b.TotalCBM) || "0"),
        0
      ),
      weight: previousPeriodBookings.reduce(
        (sum, b) => sum + parseFloat(String(b.TotalWeight) || "0"),
        0
      ),
    };

    // Calculate growth percentages
    const growth = {
      bookings: previousMetrics.bookings
        ? (
            ((currentMetrics.bookings - previousMetrics.bookings) /
              previousMetrics.bookings) *
            100
          ).toFixed(1)
        : "∞",
      volume: previousMetrics.volume
        ? (
            ((currentMetrics.volume - previousMetrics.volume) /
              previousMetrics.volume) *
            100
          ).toFixed(1)
        : "∞",
      weight: previousMetrics.weight
        ? (
            ((currentMetrics.weight - previousMetrics.weight) /
              previousMetrics.weight) *
            100
          ).toFixed(1)
        : "∞",
    };

    // Prepare data for visualization
    const data = [
      {
        name: "Bookings",
        current: currentMetrics.bookings,
        previous: previousMetrics.bookings,
        growth: growth.bookings,
      },
      {
        name: "Volume (CBM)",
        current: parseFloat(currentMetrics.volume.toFixed(2)),
        previous: parseFloat(previousMetrics.volume.toFixed(2)),
        growth: growth.volume,
      },
      {
        name: "Weight (kg)",
        current: parseFloat(currentMetrics.weight.toFixed(2)),
        previous: parseFloat(previousMetrics.weight.toFixed(2)),
        growth: growth.weight,
      },
    ];

    return {
      data,
      currentPeriod,
      previousPeriod,
      noData: false,
    };
  }, [exportData, filters.dateRange]);

  // Update the bookingsByHourAndDay useMemo to use timezone-aware dates
  const bookingsByHourAndDay = useMemo(() => {
    // Map days so that Monday=0, ..., Sunday=6 (for Y axis top to bottom)
    // dayjs().isoWeekday() gives 1 (Monday) to 7 (Sunday)
    const hourDayData: Array<{ hour: number; day: number; value: number }> = [];
    for (let y = 0; y < 7; y++) {
      for (let hour = 0; hour < 24; hour++) {
        hourDayData.push({ hour, day: y, value: 0 });
      }
    }
    getFilteredBookings().forEach((booking) => {
      // Always prefer created_at if it's available and has valid time
      if (booking.created_at && typeof booking.created_at === "string") {
        // Convert UTC timestamp to local time
        const date = convertUTCToLocal(booking.created_at);

        // Skip invalid dates
        if (!date.isValid()) return;

        // Map Monday=0, ..., Sunday=6
        const isoDay = date.isoWeekday(); // 1 (Mon) - 7 (Sun)
        const yDay = isoDay - 1; // 0 (Mon) - 6 (Sun)
        const hour = date.hour();
        const entry = hourDayData.find(
          (d) => d.hour === hour && d.day === yDay
        );
        if (entry) {
          entry.value += 1;
        }
      }
      // Fall back to CreatedDate when created_at is not available
      else if (booking.CreatedDate) {
        // Convert UTC timestamp to local
        const date = convertUTCToLocal(String(booking.CreatedDate));

        // Skip invalid dates
        if (!date.isValid()) return;

        // Generate a deterministic hour as fallback
        const identifier = String(booking.ID || booking.Reference || "");
        const deterministicHour = generateDeterministicHour(identifier);

        // Map Monday=0, ..., Sunday=6
        const isoDay = date.isoWeekday(); // 1 (Mon) - 7 (Sun)
        const yDay = isoDay - 1; // 0 (Mon) - 6 (Sun)
        const entry = hourDayData.find(
          (d) => d.hour === deterministicHour && d.day === yDay
        );
        if (entry) {
          entry.value += 1;
        }
      }
    });
    return hourDayData;
  }, [exportData, filters.dateRange]);

  // Calculate overall growth trend
  const growthTrend = useMemo(() => {
    if (
      !periodComparison ||
      periodComparison.noData ||
      !periodComparison.data.length
    ) {
      return { growth: 0, trend: "neutral" as const };
    }

    // Use the bookings growth as the main indicator
    const bookingsGrowth = periodComparison.data.find(
      (item) => item.name === "Bookings"
    );

    if (!bookingsGrowth) {
      return { growth: 0, trend: "neutral" as const };
    }

    const growthValue = parseFloat(String(bookingsGrowth.growth));

    return {
      growth: Math.abs(growthValue).toFixed(1),
      trend:
        growthValue > 0
          ? ("positive" as const)
          : growthValue < 0
          ? ("negative" as const)
          : ("neutral" as const),
    };
  }, [periodComparison]);

  // Function to toggle full screen mode
  const toggleFullScreenDashboard = useCallback(() => {
    if (!document.fullscreenElement) {
      dashboardRef.current?.requestFullscreen();
      setFullScreenMode(true);
    } else {
      document.exitFullscreen();
      setFullScreenMode(false);
    }
  }, []);

  // Add fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullScreenMode(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <FullScreenContainer
      ref={dashboardRef}
      className={fullScreenMode ? "fullscreen-dashboard" : ""}
    >
      <Container>
        {/* Breadcrumb */}
        <AdminNavBar />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Title level={3}>Shipping Analytics Dashboard</Title>
          <Space>
            <LastUpdateBadge>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Last updated: {lastUpdated}
              {newBookingsCount > 0 && (
                <Badge
                  count={newBookingsCount}
                  style={{ marginLeft: 8 }}
                  title={`${newBookingsCount} new booking(s) since last refresh`}
                />
              )}
            </LastUpdateBadge>
            <Button
              icon={<ReloadOutlined />}
              onClick={refreshData}
              title="Refresh data"
            >
              Refresh
            </Button>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: 8 }}>Real-time:</span>
              <Switch
                checked={realTimeEnabled}
                onChange={setRealTimeEnabled}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
            <Button
              icon={<FullscreenOutlined />}
              onClick={toggleFullScreenDashboard}
              title={fullScreenMode ? "Exit full screen" : "Enter full screen"}
            >
              {fullScreenMode ? "Exit Fullscreen" : "Fullscreen"}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadCSV}
              title="Export as CSV"
            >
              Export CSV
            </Button>
          </Space>
        </div>

        <FiltersContainer>
          <div>
            <div style={{ marginBottom: "5px", fontSize: "14px" }}>
              Booking created between dates:
            </div>
            <RangePicker
              style={{ marginTop: 5 }}
              value={
                filters.dateRange
                  ? [dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]
                  : undefined
              }
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilters({
                    ...filters,
                    dateRange: [
                      dates[0].format("YYYY-MM-DD"),
                      dates[1].format("YYYY-MM-DD"),
                    ],
                  });
                  // Clear time range selection when user manually selects dates
                  setTimeRange("custom");
                } else {
                  setFilters({
                    ...filters,
                    dateRange: undefined,
                  });
                }
              }}
              getPopupContainer={() => dashboardRef.current || document.body}
            />
          </div>

          <Select
            style={{ width: 150, marginTop: 5 }}
            placeholder="Time Range"
            value={timeRange}
            onChange={handleTimeRangeChange}
            getPopupContainer={() => dashboardRef.current || document.body}
          >
            <Option value="today">Today</Option>
            <Option value="yesterday">Yesterday</Option>
            <Option value="this-week">This Week</Option>
            <Option value="last-week">Last Week</Option>
            <Option value="this-month">This Month</Option>
            <Option value="last-month">Last Month</Option>
            <Option value="month-to-date">Month To Date</Option>
            <Option value="year-to-date">Year To Date</Option>
            <Option value="last-quarter">Last Quarter</Option>
            <Option value="last-year">Last Year</Option>
            <Option value="all-time">All Time</Option>
          </Select>
        </FiltersContainer>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={
            <div style={{ display: "flex", alignItems: "center" }}>
              {realTimeEnabled && (
                <span style={{ marginRight: 12, color: "#52c41a" }}>
                  <SyncOutlined spin /> Auto-refreshing
                </span>
              )}
            </div>
          }
        >
          {/* Overview Tab */}
          <TabPane
            tab={
              <span>
                <AreaChartOutlined /> Overview
              </span>
            }
            key="overview"
          >
            {loading ? (
              <CenteredSpin>
                <Spin size="large" />
              </CenteredSpin>
            ) : (
              <>
                {/* Key Metrics Summary */}
                <Row gutter={[16, 16]} className="dashboard-metrics">
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Total Bookings"
                        value={getFilteredBookings().length}
                        prefix={<BookOutlined />}
                      />
                    </StatsCard>
                  </Col>
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Total Volume"
                        value={totalVolume}
                        precision={2}
                        suffix="m³"
                        prefix={<InboxOutlined />}
                      />
                    </StatsCard>
                  </Col>
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Total Weight"
                        value={totalWeight}
                        precision={2}
                        suffix="kg"
                        prefix={<ShoppingOutlined />}
                      />
                    </StatsCard>
                  </Col>
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Avg Volume/Booking"
                        value={averageVolumePerBooking}
                        precision={2}
                        suffix="m³"
                        prefix={<FireOutlined />}
                      />
                    </StatsCard>
                  </Col>
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Avg Bookings/Company"
                        value={bookingsPerCompany.average}
                        precision={1}
                        prefix={<TeamOutlined />}
                      />
                    </StatsCard>
                  </Col>
                  <Col xs={24} sm={8} md={6} lg={4}>
                    <StatsCard>
                      <Statistic
                        title="Est. Products/Booking"
                        value={productsPerBooking.average}
                        precision={1}
                        prefix={<ShoppingOutlined />}
                      />
                    </StatsCard>
                  </Col>
                </Row>

                {/* Main Charts */}
                <Row
                  gutter={[16, 16]}
                  className="dashboard-charts"
                  style={{ marginBottom: 24 }}
                >
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Bookings Over Time">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={bookingTimeSeries.data}
                            margin={{
                              top: 10,
                              right: 30,
                              left: 0,
                              bottom: 30,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="label"
                              angle={-45}
                              textAnchor="end"
                              height={70}
                            />
                            <YAxis />
                            <RechartsTooltip
                              formatter={(value, name) => {
                                if (name === "total") return null;
                                return [`${value} Bookings`, `Status: ${name}`];
                              }}
                            />
                            <Legend />
                            {bookingTimeSeries.statuses.map((status, index) => (
                              <Bar
                                key={status}
                                dataKey={status}
                                name={status}
                                stackId="a"
                                fill={
                                  STATUS_COLORS[status.toLowerCase()] ||
                                  COLORS[index % COLORS.length]
                                }
                              />
                            ))}
                            {getTimeGranularity() !== "hour" && (
                              <Brush
                                dataKey="label"
                                height={30}
                                stroke="#8884d8"
                                startIndex={Math.max(
                                  0,
                                  bookingTimeSeries.data.length - 20
                                )}
                              />
                            )}
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Booking Status Distribution">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={bookingsByStatus}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              fill="#8884d8"
                              label={({ name, percent }) =>
                                `${name}: ${(percent * 100).toFixed(0)}%`
                              }
                            >
                              {bookingsByStatus.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    STATUS_COLORS[entry.name.toLowerCase()] ||
                                    COLORS[index % COLORS.length]
                                  }
                                />
                              ))}
                            </Pie>
                            <RechartsTooltip
                              formatter={(value: number, name: string) => [
                                `${value} Bookings`,
                                name,
                              ]}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Period Comparison">
                      {periodComparison.noData ? (
                        <NoDataMessage>
                          <p>
                            Select a date range to compare with previous period
                          </p>
                        </NoDataMessage>
                      ) : (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                              Comparing:{" "}
                              <strong>{periodComparison.currentPeriod}</strong>{" "}
                              vs{" "}
                              <strong>{periodComparison.previousPeriod}</strong>
                            </div>
                          </div>

                          {/* Bookings Comparison Chart */}
                          <div style={{ marginBottom: 24 }}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: "bold",
                                marginBottom: 5,
                              }}
                            >
                              Bookings
                            </div>
                            <ChartContainer style={{ height: 180 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={periodComparison.data.filter(
                                    (item) => item.name === "Bookings"
                                  )}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Bar
                                    dataKey="current"
                                    name="Current Period"
                                    fill="#82ca9d"
                                  />
                                  <Bar
                                    dataKey="previous"
                                    name="Previous Period"
                                    fill="#8884d8"
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </ChartContainer>
                          </div>

                          {/* Volume Comparison Chart */}
                          <div style={{ marginBottom: 24 }}>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: "bold",
                                marginBottom: 5,
                              }}
                            >
                              Volume (m³)
                            </div>
                            <ChartContainer style={{ height: 180 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={periodComparison.data.filter(
                                    (item) => item.name === "Volume (CBM)"
                                  )}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Bar
                                    dataKey="current"
                                    name="Current Period"
                                    fill="#82ca9d"
                                  />
                                  <Bar
                                    dataKey="previous"
                                    name="Previous Period"
                                    fill="#8884d8"
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </ChartContainer>
                          </div>

                          {/* Weight Comparison Chart */}
                          <div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: "bold",
                                marginBottom: 5,
                              }}
                            >
                              Weight (kg)
                            </div>
                            <ChartContainer style={{ height: 180 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={periodComparison.data.filter(
                                    (item) => item.name === "Weight (kg)"
                                  )}
                                  margin={{
                                    top: 5,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="name" />
                                  <YAxis />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Bar
                                    dataKey="current"
                                    name="Current Period"
                                    fill="#82ca9d"
                                  />
                                  <Bar
                                    dataKey="previous"
                                    name="Previous Period"
                                    fill="#8884d8"
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </ChartContainer>
                          </div>
                        </>
                      )}
                    </DashboardCard>
                  </Col>
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Booking Time Patterns">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <ScatterChart
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 60,
                              left: 40,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              type="number"
                              dataKey="hour"
                              name="Hour"
                              domain={[0, 23]}
                              tickCount={12}
                              tickFormatter={(hour) => `${hour}:00`}
                              label={{
                                value: "Hour of Day",
                                position: "insideBottom",
                                offset: -20,
                              }}
                            />
                            <YAxis
                              type="number"
                              dataKey="day"
                              name="Day"
                              tickCount={7}
                              domain={[0, 6]}
                              tickFormatter={(day) =>
                                [
                                  "Mon",
                                  "Tue",
                                  "Wed",
                                  "Thu",
                                  "Fri",
                                  "Sat",
                                  "Sun",
                                ][day]
                              }
                              reversed
                              label={{
                                value: "Day of Week (Mon-Sun)",
                                angle: -90,
                                position: "insideLeft",
                              }}
                            />
                            <ZAxis
                              type="number"
                              dataKey="value"
                              range={[50, 500]}
                              name="Bookings"
                            />
                            <RechartsTooltip
                              formatter={(value, name, props) => {
                                const { hour, day } = props.payload;
                                const dayName = [
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                  "Sunday",
                                ][day];
                                const formattedHour =
                                  hour < 10 ? `0${hour}:00` : `${hour}:00`;
                                return [
                                  `${value} Bookings`,
                                  `${dayName} at ${formattedHour}`,
                                ];
                              }}
                            />
                            <Scatter
                              name="Bookings by Time"
                              data={bookingsByHourAndDay}
                              fill="#8884d8"
                              shape="circle"
                              label={renderScatterLabel}
                            />
                          </ScatterChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>

          {/* Detailed Bookings Tab */}
          <TabPane
            tab={
              <span>
                <TableOutlined /> Detailed Bookings
              </span>
            }
            key="bookings"
          >
            {loading ? (
              <CenteredSpin>
                <Spin size="large" />
              </CenteredSpin>
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <DashboardCard title="Detailed Bookings">
                      <Table
                        dataSource={getFilteredBookings()}
                        rowKey="ID"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1200 }}
                        columns={[
                          {
                            title: "Reference",
                            dataIndex: "Reference",
                            key: "reference",
                            render: (text) => text || "N/A",
                          },
                          {
                            title: "Status",
                            dataIndex: "Status",
                            key: "status",
                            render: (status) => (
                              <Tag
                                color={
                                  status === "completed"
                                    ? "green"
                                    : status === "in_transit"
                                    ? "blue"
                                    : status === "processing"
                                    ? "orange"
                                    : status === "cancelled"
                                    ? "red"
                                    : "default"
                                }
                              >
                                {String(status).toUpperCase()}
                              </Tag>
                            ),
                            filters: [
                              ...new Set(exportData.map((b) => b.Status)),
                            ]
                              .filter((s): s is string => !!s)
                              .map((status) => ({
                                text: String(status).toUpperCase(),
                                value: status,
                              })),
                            onFilter: (value, record) =>
                              String(record.Status) === value,
                          },
                          {
                            title: "Created Date",
                            dataIndex: "CreatedDate",
                            key: "date",
                            render: (text) =>
                              text
                                ? dayjs(text).format("YYYY-MM-DD HH:mm")
                                : "N/A",
                            sorter: (a, b) =>
                              dayjs(a.CreatedDate).unix() -
                              dayjs(b.CreatedDate).unix(),
                            defaultSortOrder: "descend",
                          },
                          {
                            title: "Company",
                            dataIndex: "Company",
                            key: "company",
                            filters: [
                              ...new Set(exportData.map((b) => b.Company)),
                            ]
                              .filter((c): c is string => !!c)
                              .map((company) => ({
                                text: company,
                                value: company,
                              })),
                            onFilter: (value, record) =>
                              String(record.Company) === value,
                          },
                          {
                            title: "Transport Type",
                            dataIndex: "TransportType",
                            key: "transport",
                            filters: [
                              ...new Set(
                                exportData.map((b) => b.TransportType)
                              ),
                            ]
                              .filter((t): t is string => !!t)
                              .map((type) => ({
                                text: type,
                                value: type,
                              })),
                            onFilter: (value, record) =>
                              String(record.TransportType) === value,
                          },
                          {
                            title: "Volume (m³)",
                            dataIndex: "TotalCBM",
                            key: "volume",
                            render: (text) =>
                              text
                                ? parseFloat(String(text)).toFixed(2)
                                : "N/A",
                            sorter: (a, b) =>
                              parseFloat(String(a.TotalCBM) || "0") -
                              parseFloat(String(b.TotalCBM) || "0"),
                          },
                          {
                            title: "Weight (kg)",
                            dataIndex: "TotalWeight",
                            key: "weight",
                            render: (text) =>
                              text
                                ? parseFloat(String(text)).toFixed(2)
                                : "N/A",
                            sorter: (a, b) =>
                              parseFloat(String(a.TotalWeight) || "0") -
                              parseFloat(String(b.TotalWeight) || "0"),
                          },
                          {
                            title: "Consolidated",
                            dataIndex: "IsConsolidated",
                            key: "consolidated",
                            render: (text) => (text === "Yes" ? "Yes" : "No"),
                            filters: [
                              { text: "Yes", value: "Yes" },
                              { text: "No", value: "No" },
                            ],
                            onFilter: (value, record) =>
                              record.IsConsolidated === value,
                          },
                        ]}
                      />
                    </DashboardCard>
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col xs={24}>
                    <DashboardCard title="Booking Volume vs Weight">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <ScatterChart
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 10,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              type="number"
                              dataKey="TotalCBM"
                              name="Volume"
                              unit="m³"
                              label={{
                                value: "Volume (m³)",
                                position: "insideBottomRight",
                                offset: -10,
                              }}
                            />
                            <YAxis
                              type="number"
                              dataKey="TotalWeight"
                              name="Weight"
                              unit="kg"
                            />
                            <RechartsTooltip
                              cursor={{ strokeDasharray: "3 3" }}
                            />
                            <Legend />
                            <Scatter
                              name="Bookings"
                              data={getFilteredBookings().map((item) => ({
                                ...item,
                                TotalCBM: parseFloat(String(item.TotalCBM)),
                                TotalWeight: parseFloat(
                                  String(item.TotalWeight)
                                ),
                              }))}
                              fill="#8884d8"
                            />
                          </ScatterChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>

          {/* Company Analysis Tab */}
          <TabPane
            tab={
              <span>
                <TeamOutlined /> Company Analysis
              </span>
            }
            key="companies"
          >
            {loading ? (
              <CenteredSpin>
                <Spin size="large" />
              </CenteredSpin>
            ) : (
              <>
                {bookingsPerCompany.companies.length > 0 ? (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <DashboardCard title="Top Performing Companies">
                          <ChartContainer>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={topPerformingCompanies.slice(0, 12)}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 90,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="company"
                                  angle={-45}
                                  textAnchor="end"
                                  height={70}
                                />
                                <YAxis />
                                <RechartsTooltip
                                  formatter={(value, name) => [
                                    value,
                                    name === "avgVolume"
                                      ? "Avg Volume (m³)"
                                      : name === "bookings"
                                      ? "Total Bookings"
                                      : name,
                                  ]}
                                />
                                <Legend />
                                <Bar
                                  dataKey="bookings"
                                  name="Bookings"
                                  fill="#8884d8"
                                />
                                <Bar
                                  dataKey="avgVolume"
                                  name="Avg Volume (m³)"
                                  fill="#82ca9d"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </ChartContainer>
                        </DashboardCard>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col xs={24}>
                        <DashboardCard title="Top Performing Companies Details">
                          <Table
                            dataSource={topPerformingCompanies as TopCompany[]}
                            rowKey="company"
                            pagination={false}
                            scroll={{ x: 800 }}
                            columns={[
                              {
                                title: "Company",
                                dataIndex: "company",
                                key: "company-name",
                                fixed: "left",
                                width: 200,
                                render: (text: string, record: TopCompany) => (
                                  <Space>
                                    {text}
                                    <Badge count={record.bookings} />
                                  </Space>
                                ),
                              },
                              {
                                title: "Total Bookings",
                                dataIndex: "bookings",
                                key: "bookings-count",
                                sorter: (a: TopCompany, b: TopCompany) =>
                                  a.bookings - b.bookings,
                                defaultSortOrder: "descend",
                              },
                              {
                                title: "Total Volume (m³)",
                                dataIndex: "volume",
                                key: "volume-total",
                                sorter: (a: TopCompany, b: TopCompany) =>
                                  a.volume - b.volume,
                                render: (text: number) => text.toFixed(2),
                              },
                              {
                                title: "Avg Volume/Booking (m³)",
                                dataIndex: "avgVolume",
                                key: "volume-avg",
                                sorter: (a: TopCompany, b: TopCompany) =>
                                  a.avgVolume - b.avgVolume,
                                render: (text: number) => text.toFixed(2),
                              },
                              {
                                title: "Total Weight (kg)",
                                dataIndex: "weight",
                                key: "weight-total",
                                sorter: (a: TopCompany, b: TopCompany) =>
                                  a.weight - b.weight,
                                render: (text: number) => text.toFixed(2),
                              },
                              {
                                title: "Avg Weight/Booking (kg)",
                                dataIndex: "avgWeight",
                                key: "weight-avg",
                                sorter: (a: TopCompany, b: TopCompany) =>
                                  a.avgWeight - b.avgWeight,
                                render: (text: number) => text.toFixed(2),
                              },
                            ]}
                          />
                        </DashboardCard>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <NoDataMessage>
                    <p>
                      No company data available for the selected time period
                    </p>
                  </NoDataMessage>
                )}
              </>
            )}
          </TabPane>

          {/* Performance Metrics Tab */}
          <TabPane
            tab={
              <span>
                <BarChartOutlined /> Performance Metrics
              </span>
            }
            key="performance"
          >
            {loading ? (
              <CenteredSpin>
                <Spin size="large" />
              </CenteredSpin>
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Shipment Status Distribution">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart
                            cx="50%"
                            cy="50%"
                            outerRadius="80%"
                            data={bookingsByStatus}
                          >
                            <PolarGrid />
                            <PolarAngleAxis dataKey="name" />
                            <PolarRadiusAxis />
                            <Radar
                              name="Status Count"
                              dataKey="value"
                              stroke="#8884d8"
                              fill="#8884d8"
                              fillOpacity={0.6}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                  <Col xs={24} lg={12}>
                    <DashboardCard title="Shipping Volume vs Weight">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <ScatterChart
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 20,
                              left: 20,
                            }}
                          >
                            <CartesianGrid />
                            <XAxis
                              type="number"
                              dataKey="TotalCBM"
                              name="Volume (CBM)"
                              unit="m³"
                            />
                            <YAxis
                              type="number"
                              dataKey="TotalWeight"
                              name="Weight"
                              unit="kg"
                            />
                            <RechartsTooltip
                              cursor={{ strokeDasharray: "3 3" }}
                            />
                            <Legend />
                            <Scatter
                              name="Bookings"
                              data={getFilteredBookings().map((item) => ({
                                ...item,
                                TotalCBM: parseFloat(String(item.TotalCBM)),
                                TotalWeight: parseFloat(
                                  String(item.TotalWeight)
                                ),
                              }))}
                              fill="#8884d8"
                            />
                          </ScatterChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                </Row>

                {/* Growth and Performance Indicators */}
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  <Col xs={24}>
                    <DashboardCard title="Performance Indicators">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Growth Trend"
                            value={growthTrend.growth}
                            suffix="%"
                            valueStyle={{
                              color:
                                growthTrend.trend === "positive"
                                  ? "#3f8600"
                                  : growthTrend.trend === "negative"
                                  ? "#cf1322"
                                  : "#8c8c8c",
                            }}
                            prefix={
                              growthTrend.trend === "positive" ? (
                                <ArrowUpOutlined />
                              ) : growthTrend.trend === "negative" ? (
                                <ArrowDownOutlined />
                              ) : (
                                <MinusOutlined />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Real-time Monitoring"
                            value={realTimeEnabled ? "Active" : "Inactive"}
                            valueStyle={{
                              color: realTimeEnabled ? "#52c41a" : "#8c8c8c",
                            }}
                            prefix={
                              realTimeEnabled ? (
                                <SyncOutlined spin />
                              ) : (
                                <SyncOutlined />
                              )
                            }
                          />
                        </Col>
                        <Col xs={24} sm={8}>
                          <Statistic
                            title="Last Update"
                            value={lastUpdated}
                            valueStyle={{ color: "#1890ff" }}
                          />
                        </Col>
                      </Row>
                    </DashboardCard>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>

          {/* Advanced Analytics Tab */}
          <TabPane
            tab={
              <span>
                <PieChartOutlined /> Advanced Analytics
              </span>
            }
            key="advanced"
          >
            {loading ? (
              <CenteredSpin>
                <Spin size="large" />
              </CenteredSpin>
            ) : (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={24} lg={12}>
                    <DashboardCard title="Products per Booking Distribution">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={productsPerBooking.distribution}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 5,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="products"
                              label={{
                                value: "Number of Products",
                                position: "insideBottom",
                                offset: -5,
                              }}
                            />
                            <YAxis
                              label={{
                                value: "Number of Bookings",
                                angle: -90,
                                position: "insideLeft",
                              }}
                            />
                            <RechartsTooltip
                              formatter={(value, name, props) => {
                                const { percentage } = props.payload;
                                return [
                                  `${value} Bookings (${percentage}%)`,
                                  "Frequency",
                                ];
                              }}
                            />
                            <Bar
                              dataKey="count"
                              fill="#82ca9d"
                              name="Bookings"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <DashboardCard title="Booking Creation Time Heatmap">
                      <ChartContainer>
                        <ResponsiveContainer width="100%" height="100%">
                          <ScatterChart
                            margin={{
                              top: 20,
                              right: 20,
                              bottom: 60,
                              left: 40,
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              type="number"
                              dataKey="hour"
                              name="Hour"
                              domain={[0, 23]}
                              tickCount={12}
                              tickFormatter={(hour) => `${hour}:00`}
                              label={{
                                value: "Hour of Day",
                                position: "insideBottom",
                                offset: -20,
                              }}
                            />
                            <YAxis
                              type="number"
                              dataKey="day"
                              name="Day"
                              tickCount={7}
                              domain={[0, 6]}
                              tickFormatter={(day) =>
                                [
                                  "Mon",
                                  "Tue",
                                  "Wed",
                                  "Thu",
                                  "Fri",
                                  "Sat",
                                  "Sun",
                                ][day]
                              }
                              reversed
                              label={{
                                value: "Day of Week (Mon-Sun)",
                                angle: -90,
                                position: "insideLeft",
                              }}
                            />
                            <ZAxis
                              type="number"
                              dataKey="value"
                              range={[50, 500]}
                              name="Bookings"
                            />
                            <RechartsTooltip
                              formatter={(value, name, props) => {
                                const { hour, day } = props.payload;
                                const dayName = [
                                  "Monday",
                                  "Tuesday",
                                  "Wednesday",
                                  "Thursday",
                                  "Friday",
                                  "Saturday",
                                  "Sunday",
                                ][day];
                                const formattedHour =
                                  hour < 10 ? `0${hour}:00` : `${hour}:00`;
                                return [
                                  `${value} Bookings`,
                                  `${dayName} at ${formattedHour}`,
                                ];
                              }}
                            />
                            <Scatter
                              name="Bookings by Time"
                              data={bookingsByHourAndDay}
                              fill="#8884d8"
                              shape="circle"
                              label={renderScatterLabel}
                            />
                          </ScatterChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </DashboardCard>
                  </Col>
                </Row>
              </>
            )}
          </TabPane>
        </Tabs>
      </Container>
    </FullScreenContainer>
  );
};

export default ReportsAdmin;
